import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { WorkspaceService } from 'app/core/workspace/workspace.service';
import { Workspace, WorkspaceRole } from 'app/core/workspace/workspace.types';

@Component({
    selector: 'workspaces',
    templateUrl: './workspaces.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'workspaces',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        NgClass,
        TranslocoModule
    ],
})
export class WorkspacesComponent implements OnInit, OnDestroy {
    workspaces: Workspace[] = [];
    selectedWorkspace: Workspace | null = null;
    currentUserRole: WorkspaceRole | null = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _workspaceService: WorkspaceService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to workspaces
        this._workspaceService.workspaces$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((workspaces) => {
                this.workspaces = workspaces;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to selected workspace
        this._workspaceService.selectedWorkspace$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((workspace) => {
                this.selectedWorkspace = workspace;
                this.currentUserRole = workspace?.role ?? null;
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Select a workspace
     *
     * @param workspace
     */
    selectWorkspace(workspace: Workspace): void {
        this._workspaceService.selectWorkspace(workspace._id);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item._id || index;
    }
}
